import { Injectable } from '@angular/core';
import { logPerformanceWithParams } from '@office-start/telemetry-service';
import { AsyncSubject, from, Subject } from 'rxjs';
import { take } from 'rxjs/operators';

import { InstrumentationParam, InstrumentationParams, InstrumentationService } from '../instrumentation/instrumentation.service';
import { FetchStrategy } from '../instrumentation/instrumented-http.service';
import { FileItem, FileType } from '../models/file-item.model';
import { NewFileInfo, OfficeApplicationDiagnostics } from '../models/jsapi.model';
import { GraphDriveItem } from '../models/onedrive-graph.model';
import { WdOriginType } from '../models/telemetry-constants.model';
import { BootAndLoadInfo, BootstrapperScriptInfo, DocumentInfo, UiHostPerformanceInfo, WacLoadFunction, WacPerfDumpEventName, WacPerformanceTiming, WopiFetchData } from '../models/wac-host.model';
import { MruFileItem, WopiHostData } from '../mru/mru-file-item.model';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { ClientStartupConfigService } from '../shared-context/client-startup-config.service';
import { ContextService } from '../shared-context/context.service';
import { TemplatesConfigService } from '../templates/templates-config.service';
import { ObservableHelper } from '../utilities/observable-helper.utility';
import { createWacContentInstrumentationParams } from '../utilities/wac-content-utility';
import { DocumentService } from './document.service';
import { PerformanceMeasureType, PerformanceService } from './performance.service';
import { VroomService } from './vroom.service';
import { WacContentService } from './wac-content.service';
import { XhrServiceError } from './xhr.service';

export interface PrebootedApplication {
    correlationAndBoot: [string, number];
    loadFunctionSubject: AsyncSubject<WacLoadFunction>;
    loadData: BootAndLoadInfo;
    expirationTime: number;
    finishLoad: (wopiHostData: WopiHostData, newFileInfo?: NewFileInfo) => void;
    cancelLoad: () => void;
}

@Injectable()
export abstract class UiHostBootstrapperService {
    protected readonly bootstrapperDiagnostics: Map<string, WacPerformanceTiming>;
    protected readonly applicationUrls: Map<FileType, BootstrapperScriptInfo>;
    protected readonly bootstrappersLoaded: Map<string, Promise<void>>;
    protected readonly performanceMarker: Map<number, UiHostPerformanceInfo>;

    protected index = 0;

    constructor(
        protected readonly capabilitiesService: CapabilitiesService,
        protected readonly clientStartupConfigService: ClientStartupConfigService,
        protected readonly contextService: ContextService,
        protected readonly documentService: DocumentService,
        protected readonly instrumentationService: InstrumentationService,
        protected readonly performanceService: PerformanceService,
        protected readonly templatesConfigService: TemplatesConfigService,
        protected readonly vroomService: VroomService
    ) {
        this.bootstrappersLoaded = new Map<string, Promise<void>>();
        this.bootstrapperDiagnostics = new Map<string, WacPerformanceTiming>();
        this.applicationUrls = new Map<FileType, BootstrapperScriptInfo>();
        this.performanceMarker = new Map<number, UiHostPerformanceInfo>();
    }

    public abstract prebootDocument(documentInfo: DocumentInfo);

    public abstract bootstrapItem(
        documentInfo: DocumentInfo,
        clickTime: number,
        pendingWopiFetchPromise?: Promise<WopiFetchData>
    ): BootAndLoadInfo;

    public abstract bootstrapZeroByteDocument(
        documentInfo: DocumentInfo,
        clickTime: number,
        createDataPromise: Promise<DocumentInfo>,
        templateId?: string,
        templateDriveId?: string
    ): BootAndLoadInfo;

    public abstract bootstrapApplication(
        fileType: FileType,
        wdOrigin: string
    ): PrebootedApplication;

    /**
     * Adds a the wac bootstrapper script from the given url to the page
     * TODO: Integrate with scheduler service
     * @param bootstrapperUrl: The url to bootstrapper script
     * @param app: The filetype the bootstrapper supports
     * @returns A promise that resolves once the script has loaded
     */
    public loadBootstrapper(bootstrapperUrl: string, applicationUrl: string, app: FileType, prefetchStaticResources: boolean = false): Promise<void> {
        if (this.bootstrappersLoaded.has(bootstrapperUrl)) {
            return this.bootstrappersLoaded.get(bootstrapperUrl);
        }

        const promise = new Promise<void>((resolve, reject) => {
            // TODO: What happens if multiple versions of same bootstrapper type are used? Currently reusing old script.
            this.bootstrapperDiagnostics.set(bootstrapperUrl, { startTime: Date.now() });
            this.documentService.addScriptToPage(bootstrapperUrl, () => {
                const diagnostics = this.bootstrapperDiagnostics.get(bootstrapperUrl);
                diagnostics.endTime = Date.now();
                resolve();
            }, reject, false, `${FileType[app]}_bootstrapper`);
        });

        // Prefetch static resources for wac app
        if (prefetchStaticResources) {
            this.templatesConfigService.prefetchStaticResources(app);
        }

        this.bootstrappersLoaded.set(bootstrapperUrl, promise);
        this.applicationUrls.set(app, { bootstrapperUrl, applicationUrl });
        return promise;
    }

    public convertMruInflationPromise(itemPromise: Promise<MruFileItem>, wdOrigin: string, startTime: number): Promise<WopiFetchData> {
        return itemPromise.then(item => {
            const documentInfo = WacContentService.getDocumentInfoForMruFileItem(item, wdOrigin);
            return {
                fetchDiagnostics: { startTime, endTime: Date.now() },
                wopiHostData: item.wopiData,
                fetchedDocumentInfo: documentInfo.docId && documentInfo.driveId
                    ? this.getDocumentInfoWithSharingLink(documentInfo)
                    : this.getWopiData(documentInfo, false, false, true).then(wopi => wopi.fetchedDocumentInfo)
            };
        });
    }

    /**
     * Requests wac data for the given document and attempts to bootstrap the document
     * @param documentInfo: Info needed to boot the document in the UI host
     * @returns A promise that resolves with the wopi data and the time it took to fetch it
     */
    public getWopiData(documentInfo: DocumentInfo, zeroByte: boolean = false, forceRefresh: boolean = false, fetchSharingLink: boolean = false): Promise<WopiFetchData> {
        // This is a direct nav, get wac data from vroom/graph call
        const startTime = Date.now();
        const [itemPromise, errorPromise] = this.getDriveItemWithWacData(documentInfo, zeroByte, forceRefresh);
        return itemPromise.then(item => {
            if (item) {
                const fetchDiagnostics = { startTime, endTime: Date.now() };
                const wopiHostData = VroomService.getWopiHostDataFromItem(item);
                const graphDocumentInfo = {
                    ...documentInfo,
                    docUrl: item.webUrl ?? documentInfo.docUrl,
                    driveId: item?.parentReference?.driveId ?? documentInfo.driveId,
                    docId: item?.id ?? documentInfo.docId
                };

                const fetchedDocumentInfo = fetchSharingLink ? this.getDocumentInfoWithSharingLink(graphDocumentInfo) : Promise.resolve(graphDocumentInfo);
                return Promise.resolve({ wopiHostData, fetchDiagnostics, fetchedDocumentInfo });
            } else {
                return errorPromise
                    .then(error => Promise.reject(error))
                    .catch(error => Promise.reject(error));
            }
        }).catch(error =>
            Promise.reject(error)
        );
    }

    public logPerformanceEventFromClick(bootId: number, name: string, fileType: FileType) {
        const { clickTimeMarker, instrumentationParams }: UiHostPerformanceInfo = this.performanceMarker.get(bootId) ?? {
            clickTimeMarker: PerformanceMeasureType.FromBrowserNavigationStart,
            instrumentationParams: {
                [InstrumentationParam.WacHostEntry]: this.contextService.getWdOrigin(WdOriginType.Direct),
                [InstrumentationParam.ResourceId]: this.getCorrelationIdForBootId(bootId),
                [InstrumentationParam.FileFormat]: FileItem.fileTypeNameFromType(fileType)
            }
        };

        this.performanceService.mark(`${name}.${bootId}`, clickTimeMarker, undefined, instrumentationParams);
    }

    /**
     * A user can open multiple documents within the same session so we need to generate a unique id for each document open
     * @returns A tuple contains unique correlation id for wac content and a boot id
     */
    public generateCorrelationAndBootId(existingBootId?: number): [string, number] {
        // Increment for the next document boot
        const bootId = existingBootId ?? this.index++;
        return [this.getCorrelationIdForBootId(bootId), bootId];
    }

    public logPerformanceBootStart(
        bootId: number,
        correlationId: string,
        wdOrigin: string,
        fileType: FileType
    ) {
        const instrumentationParams: InstrumentationParams = {
            [InstrumentationParam.WacHostEntry]: wdOrigin,
            [InstrumentationParam.ResourceId]: correlationId,
            [InstrumentationParam.FileFormat]: FileItem.fileTypeNameFromType[fileType]
        };

        const clickTimeMarker = this.performanceService.mark(
            `WacContentPerformanceMarker.DocumentLoadStart.${bootId}`,
            PerformanceMeasureType.NoMeasure,
            undefined,
            instrumentationParams
        );

        this.performanceMarker.set(bootId, {
            clickTimeMarker,
            instrumentationParams
        });
    }

    protected logBootPerformance(
        correlationId: string,
        documentInfo: DocumentInfo,
        jsApiPerfData: OfficeApplicationDiagnostics,
        userClickTime: number
    ) {
        // Skip logging if the perf data is invalid
        if (!jsApiPerfData || !jsApiPerfData.documentLoadedTime || !userClickTime) {
            return;
        }

        const additionalParams = {
            [InstrumentationParam.DebugInfo]: JSON.stringify(jsApiPerfData)
        };

        const params = createWacContentInstrumentationParams(correlationId, documentInfo, additionalParams);

        const latency = jsApiPerfData.documentLoadedTime - userClickTime;

        this.instrumentationService.logPerformanceEvent(WacPerfDumpEventName, latency, params);
        logPerformanceWithParams({ eventName: WacPerfDumpEventName, latency, instrumentationParams: params});
    }

    protected getDocumentInfoWithSharingLink(documentInfo: DocumentInfo) {
        return this.capabilitiesService.capabilities.wacSharingLinkEnabled
            ? this.vroomService.createSharingLink(documentInfo.driveId, documentInfo.docId)
                .then(sharingUrl => {
                    let decodedUrl;
                    try {
                        decodedUrl = decodeURIComponent(sharingUrl);
                    } catch {
                        return documentInfo;
                    }

                    return {
                        ...documentInfo,
                        docUrl: decodedUrl ?? documentInfo.docUrl
                    };
                })
                .catch(() => documentInfo)
            : Promise.resolve(documentInfo);
    }

    protected getCorrelationIdForBootId(bootId: number): string {
        return `${this.clientStartupConfigService.config.sharedClientStartupContext.correlationId}.${bootId}`;
    }

    /**
     * Requests wac data for the given document using either the Vroom or Graph service
     * @param documentInfo: Info needed to boot the document in the UI host
     * @param zeroByte: Boolean representing if the given document info is for a zero byte document
     * @returns A promise that resolves to a GraphDriveItem with the necessary wac data populated.
     */
    protected getDriveItemWithWacData(documentInfo: DocumentInfo, zeroByte: boolean, forceRefresh: boolean): [Promise<GraphDriveItem>, Promise<XhrServiceError<GraphDriveItem>>] {
        const { docId, driveId, docUrl, fileType } = documentInfo;
        const action = zeroByte ? 'editnew' : 'open';

        const fetchWacItem = driveId && docId
            ? (strategy: FetchStrategy) => this.vroomService.getItemWithWacData(docUrl, driveId, docId, strategy, action, forceRefresh)
            : (strategy: FetchStrategy) => this.vroomService.getSharedItemWithoutThumbnail(docUrl, fileType, null, strategy, true, action, forceRefresh);

        const errorSubject = new Subject<any>();
        const driveItemResult = forceRefresh
            ? from(fetchWacItem(FetchStrategy.NetworkOnly))
            : ObservableHelper.createResultsWithCacheObservable(
                fetchWacItem(FetchStrategy.CacheOnly),
                fetchWacItem(FetchStrategy.NetworkFirst),
                undefined,
                reason => errorSubject.error(reason)
            ).results;

        // Default to using driveId and docId to fetch the wac data, but attempt to use the shares api if those aren't available or if zeroByte document
        return [driveItemResult.pipe(take(1)).toPromise(), errorSubject.toPromise()];
    }
}
